import { Form } from 'antd';
import { Download, Funnel } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useAuth } from '@/contexts/Auth';
import { ExportReports, ExportReportsProps } from '@/Export';
import { useExcelExport } from '@/store/salles/GetExcel/useExcelExport';
import { ReportType } from '@/store/salles/CreateExcel/useCreateExcel';
import { useActiveFilters } from '../../../pages/Category/ListCategory/hooks/useActiveFilters';
import { ISearchData } from '../../../pages/Category/ListCategory/types';
import * as S from './styled';
import { MONITORED_FIELDS } from './const';

type ExportFilters = Partial<ISearchData> | Record<string, unknown>;

type ShowFilterType = 'filter' | 'export' | null;

type IProps = {
    children?: React.ReactNode;
    filters?: ExportFilters;
    rightExtra?: React.ReactNode;
    disabled?: boolean;
    exportEnable?: boolean;
    type?: ReportType;
    exportLabel?: string;
    exportContent?: React.ReactNode;
    exportProps?: Pick<
        ExportReportsProps,
        'subtitle' | 'generateLabel' | 'extraActions'
    >;
    monitoredFields?: string[];
};

export const ListActionsBar = React.memo(
    ({
        children,
        filters,
        rightExtra,
        disabled,
        exportEnable = true,
        type = 'category',
        exportLabel = 'Categorias',
        exportContent,
        exportProps,
        monitoredFields = MONITORED_FIELDS,
    }: IProps) => {
        const { data: dataAuth } = useAuth();
        const form = Form.useFormInstance();

        const showFilter = Form.useWatch('showFilter', form);

        const isExportActive = showFilter === 'export';
        const isFilterActive = showFilter === 'filter';

        const handleToggle = (type: ShowFilterType) => {
            if (disabled) return;

            const current = form.getFieldValue('showFilter');

            form.setFieldsValue({
                showFilter: current === type ? null : type,
            });
        };

        const { data } = useExcelExport({
            userId: dataAuth?.user?.id,
            type,
            enabled: exportEnable && !exportContent,
        });

        const quantityExport = data?.data?.length ?? 0;

        const activeFiltersCount = useActiveFilters(form, monitoredFields);

        return (
            <S.ListActionsArea>
                <S.ListActionsBarContainer>
                    <Form.Item name="showFilter" noStyle>
                        <input type="hidden" />
                    </Form.Item>

                    <S.ListActions>
                        <S.ListActionButton
                            size="middle"
                            icon={<Funnel size={16} />}
                            onClick={() => handleToggle('filter')}
                            $active={isFilterActive}
                            disabled={disabled}
                        >
                            Filtros
                            <AnimatePresence>
                                {activeFiltersCount > 0 && (
                                    <S.FilterQnt
                                        as={motion.div}
                                        initial={{
                                            opacity: 0,
                                            scale: 0.9,
                                            y: -4,
                                        }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.9, y: -4 }}
                                        transition={{
                                            duration: 0.18,
                                            ease: 'easeOut',
                                        }}
                                        className="quantity"
                                    >
                                        {activeFiltersCount}
                                    </S.FilterQnt>
                                )}
                            </AnimatePresence>
                            <S.Chevron size={16} $active={isFilterActive} />
                        </S.ListActionButton>
                        {exportEnable && (
                            <S.ListActionButton
                                size="middle"
                                icon={<Download size={16} />}
                                onClick={() => handleToggle('export')}
                                $active={isExportActive}
                                disabled={disabled}
                            >
                                Exportar
                                <AnimatePresence>
                                    {!exportContent && quantityExport > 0 && (
                                        <S.FilterQnt
                                            as={motion.div}
                                            layout
                                            animate={{ scale: [1, 1.2, 1] }}
                                            exit={{ scale: 0.6, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="quantity"
                                        >
                                            {quantityExport}
                                        </S.FilterQnt>
                                    )}
                                </AnimatePresence>
                                <S.Chevron size={16} $active={isExportActive} />
                            </S.ListActionButton>
                        )}
                    </S.ListActions>
                    {rightExtra && (
                        <S.ListActionParamsArea>
                            {rightExtra}
                        </S.ListActionParamsArea>
                    )}
                </S.ListActionsBarContainer>
                <S.ShowFiltersArea $active={showFilter === 'filter'}>
                    {children}
                </S.ShowFiltersArea>
                {exportEnable && (
                    <S.ShowFiltersArea $active={showFilter === 'export'}>
                        {exportContent ?? (
                            <ExportReports
                                params={{
                                    reportType: type,
                                    ...filters,
                                }}
                                labelItems={exportLabel}
                                {...exportProps}
                            />
                        )}
                    </S.ShowFiltersArea>
                )}
            </S.ListActionsArea>
        );
    },
);
